/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Mar 13 2024
 **/

import { fromEvent } from "rxjs";
import { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { debounce } from "lodash-es";

import { EventHandler, useEventHandler, useEventHandlers } from "@rfice/eventhandler";
import { Configure, Utility } from "@/frameworks/libraries/utilities";
import { useAutoEvent } from "@/frameworks/libraries/hooks";

import { EventID, SidebarID } from "@/frameworks/core/commons";
import { useSidebarActions } from "@/frameworks/core/hooks";
import { UnitySceneManager, useUnityHandler } from "@/frameworks/core/unity";
import { IsConferenceEdittedAtom } from "@/frameworks/core/components";
import { usePersistActions, usePersistCurrentSpace } from "@/frameworks/core/persistence";
import { useStompClient } from "@/frameworks/libraries";
import { useChatRoomListActions } from "@/frameworks/core/chat";
// import { useStompManager } from "@/frameworks/libraries";

const LOG_TAG = "[CommonSpaceProvider]";

export default function CommonSpaceProvider() {
	const [isEdittedAtom, setEdittedAtom] = useAtom(IsConferenceEdittedAtom);
	const isEdittedAtomRef = useRef<boolean>(false);
	const sidebarActions = useSidebarActions();
	const { getGroupSpaceChatList } = useChatRoomListActions();
	const { spaces, users, unity } = usePersistActions();
	const currentSpaceID = usePersistCurrentSpace()?.spaceId ?? null;
	// const stompNetwork = useStompManager();

	const findSpaceInfo = (spaceID: SPACE_ID) => {
		return spaces.get_space(spaceID);
	};

	useEffect(() => {
		isEdittedAtomRef.current = isEdittedAtom;
	}, [isEdittedAtom]);

	// use debounce to prevent api leave space (meeting case) called multi times at the same time due to useEffect on line 126, exit space event line 273 & useEventHandler on line 277 and also make sure logic on line 81 goes right
	const leaveSpace = debounce(async () => {
		const isMeetingType = Boolean(users.get_user(Configure.userID)?.currentConference);

		if (isMeetingType) {
			if (isEdittedAtomRef.current) {
				// ongoing meeting editting doesn't need to call api leave meeting
				setEdittedAtom(false);
			}
		}
		const unityInfo = unity.get_my_current_space();
		console.debug(LOG_TAG, "leaveSpace", unityInfo);
		if (unityInfo) {
			const myCurrentSpace = formatMyCurrnetSpace(unityInfo);
			if (myCurrentSpace) {
				UnitySceneManager.instance.unity_leave_process("CommonSpaceProvider", myCurrentSpace);
			}
		}
	}, 500);

	useUnityHandler(
		"enterSpace",
		{
			onEnterSpace: ({ spaceID }) => {
				const currentMe = users.get_user(Configure.userID);
				if (currentMe?.authority === "GUEST") {
					sidebarActions.show(SidebarID.Space);
					sidebarActions.show(SidebarID.SpacePanel);
					EventHandler.emit(EventID.OPEN_SPACE_PANEL, { spaceID: spaceID });
				}
			}
		},
		[]
	);

	/**
	 * @description don't use core fetch interface when beforeunload event is triggered,
	 * use beacons instead
	 */
	const leaveSpaceByBeacon = async (spaceID: SPACE_ID, meetingType: IPC.Spaces.ExitSpace["meetingType"]) => {
		if (meetingType === "CONFERENCE") {
			Utility.sendData(`/meeting/conference/${spaceID}/leave`, {});
		} else {
			const spaceInfo = findSpaceInfo(spaceID);

			if (spaceInfo) {
				// stompNetwork.unsubscribe(Topics.Room(spaceInfo.generalRoomId ?? ""));

				if (spaceInfo.feSpaceType === "MY_ROOM") {
					Utility.sendData(`/api/my_room/${spaceInfo.spaceId}/leave`, {});
				} else {
					Utility.sendData(`/api/spaces/${spaceInfo.spaceId}/leave`, {});
				}
			}
		}
	};

	//비정상적인 종료시에 스페이스를 나가도록 처리
	useEffect(() => {
		return () => {
			leaveSpace();
		};
	}, []);
	/**
	 * 유니티가 사용자에 의해 강제로 종료되었을 때, 스페이스를 나가도록 처리
	 */
	useEventHandler(
		"UnityDomUnmount",
		() => {
			leaveSpace();
		},
		[]
	);

	useAutoEvent(
		[
			{
				event: fromEvent(window, "beforeunload"),
				handler: () => {
					if (currentSpaceID) {
						leaveSpaceByBeacon(currentSpaceID, findSpaceInfo(currentSpaceID) ? "SPACE" : "CONFERENCE");
					}
				}
			}
		],
		[currentSpaceID]
	);

	useStompClient({
		onMessageReceived(message) {
			const messageType = message.type as PushServices.Group.MessageIDs | PushServices.Space.MessageIDs;
			switch (messageType) {
				case "spaceInviteProcessed":
				case "spaceInfoUpdated":
				case "groupInfoUpdated":
				case "groupCreated": {
					spaces.initialize();
					break;
				}
				default: {
					break;
				}
			}
		}
	});

	useEventHandlers({
		[EventID.onGroupCommand]: (type: string) => {
			switch (type) {
				case "create":
				case "delete":
				case "approve_invited":
				case "cancel_invite":
				case "invite":
					spaces.initialize();
					break;
				default: {
					break;
				}
			}
		},
		[EventID.onSpaceCommand]: (type: string) => {
			switch (type) {
				case "create":
				case "approve_invited":
				case "cancel_invite":
				case "invite":
					spaces.initialize();
					break;
				case "leave":
					spaces.initialize();
					getGroupSpaceChatList();
					break;
			}
		}
	});

	return null;
}

function formatMyCurrnetSpace(
	unityInfo: SwaggerModels.CurrentConferenceResponseVO | SwaggerModels.UserSpaceResponseVO
): IPC.Spaces.ExitSpace | null {
	if ("spaceId" in unityInfo) {
		return {
			spaceID: unityInfo.spaceId || "",
			meetingType: "SPACE",
			sceneType: Number(unityInfo.spaceScene.sceneType?.typeNumber)
		};
	}
	if ("conferenceId" in unityInfo) {
		return {
			spaceID: unityInfo.conferenceId || "",
			meetingType: "CONFERENCE",
			sceneType: 0
		};
	}
	return null;
}
